declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: unknown;
      queue?: unknown[];
      loaded?: boolean;
      version?: string;
      push?: unknown;
    };
    _fbq?: Window["fbq"];
  }
}

let initialized = false;
let activePixelId: string | undefined;

/**
 * Campos de Advanced Matching aceitos pelo fbq.
 * Passar valores CRUS — o próprio fbq normaliza e faz SHA-256 antes de enviar.
 */
export interface PixelAdvancedMatching {
  em?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  country?: string;
  external_id?: string;
}

export interface PixelTrackOpts {
  /** ID de dedup compartilhado com o CAPI server-side. Sem isso o Meta conta duplicado. */
  eventID?: string;
  /** Re-inicializa o pixel com user data antes do track — pro Meta casar pessoa nesse evento. */
  userData?: PixelAdvancedMatching;
}

function applyUserData(data: PixelAdvancedMatching): void {
  if (typeof window === "undefined" || !window.fbq || !activePixelId) return;
  window.fbq("init", activePixelId, data);
}

export function initPixel(pixelId: string | undefined): void {
  if (typeof window === "undefined") return;
  if (!pixelId) return;
  if (initialized || window.fbq) return;

  /* eslint-disable */
  (function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode?.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */

  window.fbq?.("init", pixelId);
  window.fbq?.("track", "PageView");
  activePixelId = pixelId;
  initialized = true;
}

export function trackEvent(name: string, data?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  if (!window.fbq) return;
  if (data) {
    window.fbq("track", name, data);
  } else {
    window.fbq("track", name);
  }
}

/** Custom event (não-standard do Meta). Para eventos do nosso domínio como "MQL". */
export function trackCustom(name: string, data?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  if (!window.fbq) return;
  if (data) {
    window.fbq("trackCustom", name, data);
  } else {
    window.fbq("trackCustom", name);
  }
}

export function trackLead(
  data?: Record<string, unknown>,
  opts?: PixelTrackOpts
): void {
  if (typeof window === "undefined" || !window.fbq) return;
  if (opts?.userData) applyUserData(opts.userData);
  const eventOpts = opts?.eventID ? { eventID: opts.eventID } : undefined;
  if (eventOpts) window.fbq("track", "Lead", data ?? {}, eventOpts);
  else if (data) window.fbq("track", "Lead", data);
  else window.fbq("track", "Lead");
}

export function trackViewContent(data?: Record<string, unknown>): void {
  trackEvent("ViewContent", data);
}

export function trackCompleteRegistration(data?: Record<string, unknown>): void {
  trackEvent("CompleteRegistration", data);
}

export function trackInitiateCheckout(data?: Record<string, unknown>): void {
  trackEvent("InitiateCheckout", data);
}

/**
 * Disparado quando o lead marca que NÃO atua no mercado (carteira "nao_tem").
 * Substitui o Lead padrão — se disparássemos "Lead" pra ele, o Meta contaria
 * como conversão boa e otimizaria a campanha buscando mais gente assim.
 */
export function trackLeadDesqualificado(
  data?: Record<string, unknown>,
  opts?: PixelTrackOpts
): void {
  if (typeof window === "undefined" || !window.fbq) return;
  if (opts?.userData) applyUserData(opts.userData);
  const eventOpts = opts?.eventID ? { eventID: opts.eventID } : undefined;
  if (eventOpts) window.fbq("trackCustom", "LeadDesqualificado", data ?? {}, eventOpts);
  else if (data) window.fbq("trackCustom", "LeadDesqualificado", data);
  else window.fbq("trackCustom", "LeadDesqualificado");
}

/** Disparado quando lead se qualifica como MQL (carteira ≥ R$ 5M). */
export function trackMQL(
  data?: Record<string, unknown>,
  opts?: PixelTrackOpts
): void {
  if (typeof window === "undefined" || !window.fbq) return;
  if (opts?.userData) applyUserData(opts.userData);
  const eventOpts = opts?.eventID ? { eventID: opts.eventID } : undefined;
  if (eventOpts) window.fbq("trackCustom", "MQL", data ?? {}, eventOpts);
  else if (data) window.fbq("trackCustom", "MQL", data);
  else window.fbq("trackCustom", "MQL");
}
